import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  List,
  ListItemText,
  ListItemIcon,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Divider,
  styled,
} from '@mui/material';
import { MatchData } from '@/interfaces/liveGamesType';
import { FiStar, FiSearch, FiChevronRight } from 'react-icons/fi';
import { League } from '../../types/index';
import { LeagueItem } from '../../styles';
import LeaguesListSkeleton from './LeaguesListSkeleton';

interface LeaguesListProps {
  leagues: League[];
  favorites: string[];
  toggleFavorite: (id: string) => void;
  matches: MatchData[];
  selectedLeagueId: string | null;
  onSelectLeague: (id: string) => void;
  isLoading: boolean;
}

const NeonBox = styled(Box)`
  padding: 16px;
  height: 100%;
  overflow-y: auto;
  position: relative;
  background: rgba(20, 20, 25, 0.7);
  border: 1px solid rgba(42, 244, 151, 0.2);
  box-shadow: 0 0 15px rgba(42, 244, 151, 0.2);
  backdrop-filter: blur(5px);
  border-radius: 8px;
`;

const LeaguesList: React.FC<LeaguesListProps> = ({
  leagues,
  favorites,
  toggleFavorite,
  matches,
  selectedLeagueId,
  onSelectLeague,
  isLoading
}) => {
  const [searchText, setSearchText] = useState('');
  const [filteredLeagues, setFilteredLeagues] = useState<any[]>([]);
  const [filteredFavorites, setFilteredFavorites] = useState<any[]>([]);
  const searchTimeoutRef = useRef<number | null>(null);

  const matchLeagues = useMemo(() => {
    const uniqueLeagues = new Map();
    
    matches.forEach(match => {
      if (!uniqueLeagues.has(match.league_id)) {
        uniqueLeagues.set(match.league_id, {
          id: match.league_id,
          name: match.league_name,
          logo: match.league_logo || `https://ui-avatars.com/api/?name=${match.league_name}`,
          country: match.country_name
        });
      }
    });
    
    // Converter para array
    const leaguesArray = Array.from(uniqueLeagues.values());
    
    // Ordenar com Brasil em primeiro
    return leaguesArray.sort((a, b) => {
      // Colocar ligas brasileiras primeiro
      if (a.country.toLowerCase().includes('brazil')) {
        return -1;
      }
      if (b.country.toLowerCase().includes('brazil')) {
        return 1;
      }
      
      // Ordem alfabética para as demais
      return a.name.localeCompare(b.name);
    });
  }, [matches]);
  
  // Separa as ligas favoritas
  const favoriteLeagues = useMemo(() => {
    return matchLeagues.filter(league => 
      favorites.includes(`${league.id}-${league.name}`)
    );
  }, [matchLeagues, favorites]);

  useEffect(() => {
    // Inicializa os resultados filtrados com todas as ligas
    setFilteredLeagues(matchLeagues);
    setFilteredFavorites(favoriteLeagues);
  }, [matchLeagues, favoriteLeagues]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    
    // Limpa o timeout anterior se existir
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Define um novo timeout de 300ms
    searchTimeoutRef.current = setTimeout(() => {
      // Filtra as ligas com base no texto digitado
      const filtered = matchLeagues.filter(league => 
        league.name.toLowerCase().includes(value.toLowerCase()) || 
        league.country.toLowerCase().includes(value.toLowerCase())
      );
      
      // Filtra as ligas favoritas com base no texto digitado
      const filteredFavs = favoriteLeagues.filter(league => 
        league.name.toLowerCase().includes(value.toLowerCase()) || 
        league.country.toLowerCase().includes(value.toLowerCase())
      );
      
      setFilteredLeagues(filtered);
      setFilteredFavorites(filteredFavs);
    }, 100);
  };

  // Limpa o timeout quando o componente é desmontado
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const renderLeagueItem = (league: any) => (
    <LeagueItem 
      key={league.id}
      selected={selectedLeagueId === league.id}
      onClick={() => onSelectLeague(league.id)}
      sx={{
        bgcolor: selectedLeagueId === league.id ? 'action.selected' : 'inherit',
        '&:hover': {
          bgcolor: selectedLeagueId === league.id ? 'action.selected' : 'action.hover',
        }
      }}
    >
      <ListItemIcon>
        <img
          src={league.logo}
          alt={league.name}
          style={{ width: 24, height: 24, marginRight: 10 }}
          onError={(e: any) => { e.target.src = `https://ui-avatars.com/api/?name=${league.name}` }}
        />
      </ListItemIcon>
      <ListItemText primary={league.name} secondary={league.country} />
      <IconButton
        edge="end"
        size="small"
        onClick={(e) => {
          e.stopPropagation(); // Evitar que o clique propague para o item da lista
          toggleFavorite(`${league.id}-${league.name}`);
        }}
      >
        {favorites.includes(`${league.id}-${league.name}`)
          ? <FiStar fill="currentColor" />
          : <FiStar />
        }
      </IconButton>
    </LeagueItem>
  );

  if (isLoading) {
    return <LeaguesListSkeleton />;
  }

  return (
    <NeonBox component="section" aria-label="Competições">
      <Typography variant="h6" gutterBottom>
        Principais competições
      </Typography>

      <TextField
        placeholder="Buscar liga..."
        size="small"
        fullWidth
        sx={{ mb: 2 }}
        value={searchText}
        onChange={handleSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <FiSearch />
            </InputAdornment>
          ),
        }}
      />

      {filteredFavorites.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ my: 1 }}>
            Favoritos
          </Typography>
          <List disablePadding>
            {filteredFavorites.map(renderLeagueItem)}
          </List>
          <Divider sx={{ my: 2 }} />
        </>
      )}

      <Typography variant="subtitle2" sx={{ my: 1 }}>
        Todas as ligas
      </Typography>
      <List disablePadding>
        {filteredLeagues
          .filter(league => !favorites.includes(`${league.id}-${league.name}`))
          .map(renderLeagueItem)}
      </List>

      {/* <Box textAlign="center" mt={2}>
        <Button
          variant="text"
          color="primary"
          endIcon={<FiChevronRight />}
        >
          Mostrar mais
        </Button>
      </Box> */}
    </NeonBox>
  );
};

export default LeaguesList;